import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Shield, UserPlus, Delete, ChevronDown, User, Check, Loader2, Store } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/config/branding";
import { StoreGate, StoreBadge, initialStoreSlug, resolveStore, rememberStore, forgetStore } from "@/components/auth/StoreGate";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function PinDot({ filled }) {
  return (
    <div
      className={`w-4 h-4 rounded-full transition-all duration-200 ${
        filled
          ? "bg-cream-900 scale-110"
          : "border-2 border-th-border"
      }`}
    />
  );
}

function PinPad({ pin, onPinChange, onSubmit, loading }) {
  const handleKey = useCallback((key) => {
    if (key === "backspace") {
      onPinChange((prev) => prev.slice(0, -1));
    } else if (key === "enter") {
      onSubmit();
    } else if (pin.length < 8) {
      onPinChange((prev) => prev + key);
    }
  }, [pin.length, onPinChange, onSubmit]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key >= "0" && e.key <= "9") handleKey(e.key);
      else if (e.key === "Backspace") handleKey("backspace");
      else if (e.key === "Enter") handleKey("enter");
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleKey]);

  const keys = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["backspace", "0", "enter"],
  ];

  return (
    <div className="space-y-5">
      {/* PIN dots */}
      <div className="flex items-center justify-center gap-3 py-3">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <PinDot key={`dot-${i}`} filled={i < pin.length} />
        ))}
      </div>

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-2.5 max-w-[280px] mx-auto">
        {keys.flat().map((key) => {
          if (key === "backspace") {
            return (
              <button key={key} onClick={() => handleKey("backspace")} data-testid="pin-backspace"
                className="h-16 rounded-2xl flex items-center justify-center text-th-dim hover:bg-th-surface active:bg-th-surface transition-all active:scale-95">
                <Delete className="w-6 h-6" />
              </button>
            );
          }
          if (key === "enter") {
            return (
              <button key={key} onClick={() => handleKey("enter")} disabled={pin.length < 4 || loading} data-testid="pin-enter"
                className={`h-16 rounded-2xl flex items-center justify-center transition-all active:scale-95 ${
                  pin.length >= 4
                    ? "bg-cream-900 hover:bg-cream-900/90 text-white"
                    : "text-th-ghost cursor-not-allowed"
                }`}>
                {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Check className="w-6 h-6" />}
              </button>
            );
          }
          return (
            <button key={key} onClick={() => handleKey(key)} data-testid={`pin-key-${key}`}
              className="h-16 rounded-2xl text-2xl font-semibold text-cream-900 hover:bg-th-surface active:bg-th-surface transition-all active:scale-95 select-none">
              {key}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const [store, setStore] = useState(null); // {slug, name} — the ONE store this login page serves
  const [storeChecking, setStoreChecking] = useState(() => !!initialStoreSlug());
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false); // true only after a successful 200 response
  const retryRef = useRef(0);

  // Resolve the remembered / ?store= slug once; unknown or inactive store → ask for Store ID
  useEffect(() => {
    const slug = initialStoreSlug();
    if (!slug) return;
    resolveStore(slug)
      .then((s) => { rememberStore(s); setStore(s); })
      .catch((e) => { if (e?.response?.status === 404) forgetStore(); })
      .finally(() => setStoreChecking(false));
  }, []);

  const switchStore = () => {
    forgetStore();
    setStore(null); setUsers([]); setSelectedUser(null); setPin(""); setUsersLoaded(false); setUsersError(false);
    if (window.location.search) window.history.replaceState(null, "", "/login");
  };

  // Auto-retry with backoff (1s/2s/4s) on transient failure; NEVER clears an already-loaded list
  const loadUsers = useCallback(() => {
    if (!store?.slug) return;
    setUsersLoading(true);
    axios.get(`${API}/api/auth/users`, { params: { store: store.slug }, timeout: 12000 })
      .then((res) => {
        retryRef.current = 0;
        setUsers(res.data.users || []);
        setUsersLoaded(true);
        setUsersError(false);
      })
      .catch(() => {
        setUsersError(true);
        if (retryRef.current < 3) {
          const delay = [1000, 2000, 4000][retryRef.current] + Math.round(Math.random() * 300);
          retryRef.current += 1;
          setTimeout(loadUsers, delay);
        }
      })
      .finally(() => setUsersLoading(false));
  }, [store?.slug]);
  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => {
    try {
      if (sessionStorage.getItem("logout_reason") === "idle") {
        sessionStorage.removeItem("logout_reason");
        toast.info("You were signed out after 30 minutes of inactivity. Your open shift is still active — log in to continue.", { duration: 9000, id: "idle-logout-notice" });
      }
    } catch { /* */ }
  }, []);
  const manualRetry = () => { retryRef.current = 0; loadUsers(); };

  const handleSubmit = async () => {
    if (!selectedUser || pin.length < 4) return;
    setLoading(true);
    try {
      await login(selectedUser.username, pin, store?.slug);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const selectUser = (user) => {
    setSelectedUser(user);
    setPin("");
    setDropdownOpen(false);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    return parts.length > 1
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1fr_1fr] pwa-safe-top" data-testid="login-page">
      {/* Left side - User selection + PIN pad */}
      <div className="relative flex flex-col items-center justify-center px-5 sm:px-10 py-8 overflow-y-auto bg-th-bg">

        <a href="/platform/login" data-testid="admin-login-btn"
          className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-th-surface hover:bg-th-border text-th-dim hover:text-cream-900 z-10">
          <Shield className="w-3.5 h-3.5" /> Admin
        </a>
        <button onClick={toggleLang} data-testid="login-lang-toggle"
          className="absolute top-4 right-4 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors bg-th-surface hover:bg-th-border text-th-dim hover:text-cream-900 z-10">
          {lang === "en" ? "عربي" : "EN"}
        </button>

        <div className="w-full max-w-sm space-y-6">
          {/* Logo */}
          <div className="text-center">
            <img src={BRAND.logos.wordmark} alt={BRAND.name} className="h-10 mx-auto mb-4 object-contain" />
            <h1 className="text-2xl font-bold text-cream-900 mb-1">{t("login_welcome")}</h1>
            <p className="text-th-dim text-sm">{store ? t("login_sub") : t("login_store_sub")}</p>
          </div>

          {storeChecking ? (
            <div className="flex items-center justify-center gap-2 py-8 text-th-dim text-sm" data-testid="store-checking">
              <Loader2 className="w-4 h-4 animate-spin" /> {t("loading")}
            </div>
          ) : !store ? (
            <StoreGate t={t} onResolved={setStore} />
          ) : (
          <>
          <StoreBadge store={store} t={t} onSwitch={switchStore} />

          {/* User Dropdown */}
          <div className="relative" data-testid="user-dropdown-wrapper">
            <button onClick={() => setDropdownOpen(!dropdownOpen)} data-testid="user-dropdown-trigger"
              className="w-full flex items-center gap-3 p-3.5 rounded-2xl bg-th-card border border-th-border hover:border-th-accent hover:bg-th-surface transition-all text-left">
              {selectedUser ? (
                <>
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-clay-500 to-clay-700 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md">
                    {getInitials(selectedUser.full_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-cream-900 font-semibold text-sm truncate">{selectedUser.full_name}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-11 h-11 rounded-xl bg-th-surface flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-th-dim" />
                  </div>
                  <div className="flex-1">
                    <p className="text-th-dim text-sm">{usersLoading ? t("loading_users") : t("select_account")}</p>
                  </div>
                </>
              )}
              <ChevronDown className={`w-5 h-5 text-th-dim transition-transform flex-shrink-0 ${dropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {dropdownOpen && (
              <div className="absolute z-50 w-full mt-2 rounded-2xl bg-white border border-th-border shadow-xl shadow-th-border/30 overflow-hidden" data-testid="user-dropdown-list">
                <div className="max-h-72 overflow-y-auto p-1.5">
                  {users.length === 0 ? (
                    usersError ? (
                      <div className="text-center py-5 px-3" data-testid="users-error-state">
                        <p className="text-sm text-amber-700 font-medium mb-2">{t("net_conn_problem")}</p>
                        <button onClick={manualRetry} disabled={usersLoading}
                          className="px-4 py-1.5 text-xs font-semibold rounded-full bg-th-surface border border-th-border text-cream-900 hover:bg-th-border/40 disabled:opacity-50"
                          data-testid="users-retry-btn">
                          {usersLoading ? t("loading_users") : t("retry")}
                        </button>
                      </div>
                    ) : usersLoaded ? (
                      <p className="text-center text-th-dim text-sm py-6" data-testid="users-empty-state">{t("no_users_created")}</p>
                    ) : (
                      <p className="text-center text-th-dim text-sm py-6" data-testid="users-loading-state">{t("loading_users")}</p>
                    )
                  ) : (
                    users.map((u, i) => (
                      <button key={`${u.username}-${i}`} onClick={() => selectUser(u)} data-testid={`user-option-${u.username}`}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                          selectedUser?.username === u.username
                            ? "bg-th-surface border border-th-border"
                            : "hover:bg-th-surface/50 border border-transparent"
                        }`}>
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-clay-500 to-clay-700 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                          {getInitials(u.full_name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-cream-900 font-medium text-sm truncate">{u.full_name}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* PIN Pad - shows when user is selected */}
          {selectedUser && (
            <div className="animate-in fade-in slide-in-from-bottom-3 duration-300">
              <p className="text-center text-th-muted text-sm mb-2">
                {t("enter_pin_for")} <span className="text-cream-900 font-medium">{selectedUser.full_name}</span>
              </p>
              <PinPad pin={pin} onPinChange={setPin} onSubmit={handleSubmit} loading={loading} />
            </div>
          )}
          </>
          )}

          {/* Trial link */}
          <div className="pt-2 space-y-2">
            <a href="/signup" data-testid="start-trial-link"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-dashed border-th-border text-sm font-medium text-th-dim hover:text-cream-900 hover:border-th-accent transition-all">
              <UserPlus className="w-4 h-4" /> {t("start_trial")}
            </a>
          </div>
        </div>
      </div>

      {/* Right side - brand hero */}
      <div className="hidden lg:flex relative overflow-hidden flex-col items-center justify-center"
        style={{ background: "linear-gradient(160deg, #EEF3F0 0%, #DDE8E1 45%, #C9DACD 100%)" }}>

        {/* Decorative background image */}
        <div className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1707944145479-12755f0434d8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1200)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        {/* Soft overlay */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(241,236,228,0.75) 0%, rgba(216,183,174,0.6) 100%)" }} />

        {/* Content */}
        <div className="relative z-10 text-center px-12 max-w-md space-y-8">
          {/* Logo mark */}
          <div className="mx-auto w-20 h-20 rounded-2xl bg-white/80 backdrop-blur-sm shadow-lg flex items-center justify-center">
            <Store className="w-10 h-10" style={{ color: "#2C4C3B" }} />
          </div>

          <div className="space-y-3">
            <h2 className="text-3xl font-bold tracking-tight" style={{ color: "#1E293B" }}>
              {BRAND.name}
            </h2>
            <p className="text-base leading-relaxed" style={{ color: "#6E6761" }}>
              Point of sale, inventory, customers, loyalty and your online shop — one system for every store.
            </p>
          </div>

          {/* Feature pills */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {["Point of Sale", "Inventory", "Online Shop", "Loyalty", "Reports"].map(f => (
              <span key={f} className="px-3.5 py-1.5 rounded-full text-xs font-medium bg-white/70 backdrop-blur-sm shadow-sm"
                style={{ color: "#2C4C3B" }}>
                {f}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
